import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Building2,
  ChevronRight,
  CreditCard,
  Crown,
  Sparkles,
  Zap,
} from "lucide-react";

import { cn } from "@/lib";
import { Badge, Button } from "@/components/ui";
import { useAuth } from "@/hooks";

const PLAN_ICONS: Record<string, typeof Sparkles> = {
  Foundation: Zap,
  Intelligence: Crown,
  "Ultra Premium": Building2,
};

const PLAN_GRADIENTS: Record<string, string> = {
  Foundation: "bg-brand",
  Intelligence: "bg-brand",
  "Ultra Premium": "bg-app-warning",
};

/**
 * The clinic's current plan, at the top of `/settings/usage` (SCRUM-208,
 * audit C-05). It used to be the "Plano & Assinatura" tab of Meu Negócio,
 * one click away from the consumption it explains; `/my-business/subscription`
 * redirects here now. Buying is `/onboarding/billing`; changing and
 * cancelling are `/settings/billing/*`.
 */
export function SubscriptionPlanPanel() {
  const { user } = useAuth();
  const subscription = user?.subscription;

  if (!subscription) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <div className="bg-brand p-4 rounded-2xl">
          <CreditCard className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-xl font-bold text-app-fg">
          Você está no período de teste
        </h3>
        <p className="text-app-fg-muted text-center max-w-md">
          Assine um plano para desbloquear todas as funcionalidades da sua
          clínica.
        </p>
        <Button
          className="bg-brand hover:bg-brand-hover text-white"
          asChild
        >
          <Link to="/onboarding/billing">
            Escolher plano <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
        </Button>
      </div>
    );
  }

  const Icon = PLAN_ICONS[subscription.name] || Sparkles;
  const gradient =
    PLAN_GRADIENTS[subscription.name] || "bg-brand";

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl border border-brand-soft bg-white shadow-sm"
      >
        <div className="absolute top-0 left-0 right-0 h-1 bg-brand" />
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div
                className={cn(
                  "w-12 h-12 rounded-xl  flex items-center justify-center shadow-lg",
                  gradient
                )}
              >
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-bold text-app-fg">
                    {subscription.name || "Plano atual"}
                  </h3>
                  <Badge
                    variant="outline"
                    className="border-brand text-brand-hover bg-brand-soft"
                  >
                    Ativo
                  </Badge>
                </div>
                <p className="text-sm text-app-fg-muted mt-0.5">
                  Ciclo {subscription.type === "YEARLY" ? "anual" : "mensal"}
                </p>
              </div>
            </div>
            {subscription.price != null && (
              <div className="text-right">
                <p className="text-2xl font-bold text-app-fg">
                  R$ {subscription.price?.toLocaleString("pt-BR")}
                </p>
                <p className="text-sm text-app-fg-muted">
                  /{subscription.type === "YEARLY" ? "ano" : "mês"}
                </p>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link
          to="/settings/billing/upgrade"
          className="group rounded-2xl border border-app-border bg-white p-5 hover:border-brand hover:shadow-md transition"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-soft flex items-center justify-center">
              <Crown className="w-5 h-5 text-brand" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-app-fg">Plano único</p>
              <p className="text-xs text-app-fg-muted">
                O Growth é o único plano da CliniSharp
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-app-fg-subtle group-hover:text-brand" />
          </div>
        </Link>

        <Link
          to="/settings/billing/cancel"
          className="group rounded-2xl border border-app-border bg-white p-5 hover:border-app-danger hover:shadow-md transition"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-app-danger/10 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-app-danger" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-app-fg">Cancelar assinatura</p>
              <p className="text-xs text-app-fg-muted">
                Encerre o plano atual
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-app-fg-subtle group-hover:text-app-danger" />
          </div>
        </Link>
      </div>
    </div>
  );
}
